import { db } from "./firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, doc, setDoc } from "firebase/firestore";
import { USER_NAME } from "./constant";
import { copyBaseUserDataTemplate } from "./config/aws";

import { hashString } from "./utils/utilities";

// Helper function to verify password
const verifyPassword = async (password, hashedPassword) => {
  const hashedInput = await hashString(password);
  return hashedInput === hashedPassword;
};

export const addUserIfNotExists = async (userName, mobileNumber, email, password, credits_amount) => {    
    try {
        const usersRef = collection(db, "Users");

        // Check if user already exists
        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return { status: false, message: "User already exists with this Email  ❌" };
        }
        
        // Hash the password using SHA-256
        const hashedPassword = await hashString(password);
        const hashedEmail = await hashString(email);

        // Add user to Firestore with email as document ID
        const userDocRef = doc(db, "Users", email);
        await setDoc(userDocRef, {
            userName,
            email,
            mobileNumber,
            hashedEmail,
            password: hashedPassword,
            createdAt: new Date(),
            credits: credits_amount,
        });

        return { status: true, userId: email, message: "User Added Successfully ✅" };

    } catch (error) {
        return { status: false, message: error.message };
    }
};


export const authenticateUser = async (email, password) => {
    try {
        const usersRef = collection(db, "Users");

        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "Invalid email or password  ❌" };
        }

        let userData = null;
        querySnapshot.forEach((doc) => {
            userData = { id: doc.id, ...doc.data() };
        });

        // Compare hashed password using SHA-256
        const passwordMatch = await verifyPassword(password, userData.password);
        if (!passwordMatch) {
            return { status: false, message: "Invalid email or password  ❌" };
        }

        return { status: true, user: userData, message: "Login Successful ✅" };

    } catch (error) {
        return { status: false, message: error.message };
    }
};

export const checkAvailableCredits = async (email, password, charges) => {
    try {
        const usersRef = collection(db, "Users");

        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "User Not Authorised ❌" };
        }

        let userData = null;
        let docId = null;
        querySnapshot.forEach((doc) => {
            userData = { id: doc.id, ...doc.data() };
            docId = doc.id;
        });

        // Compare hashed password using SHA-256
        const passwordMatch = await verifyPassword(password, userData.password);
        if (!passwordMatch) {
            return { status: false, message: "Unauthorized User ❌" };
        }
        if (userData.credits <= 0)
            return { status: false, message: "Credits not Available ❌" };

        // Decrease credits by one
        // const newCredits = userData.credits - charges;
        // await updateDoc(doc(db, "Users", docId), {
        //     credits: newCredits
        // });

        return { status: true, credits: userData.credits, message: "Credits retrieved successfully ✅" };

    } catch (error) {
        return { status: false, message: error.message };
    }
}

export const decreaseCredits = async (email, password, charges, endPoint) => {
    try {
        const usersRef = collection(db, "Users");
        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "User Not Authorised ❌" };
        }

        let userData = null;
        let docId = null;
        querySnapshot.forEach((doc) => {
            userData = { id: doc.id, ...doc.data() };
            docId = doc.id;
        });

        const endpointToCallType = {
            'create': 'countCreate',
            'bgh': 'countBgh',
            'upscale': 'countUpscale', 
            'edit': 'countEdit',
            'create-checkout-session': 'countUpgradePlan',
            'rembg': 'countNewProduct',
            'newStyle': 'countNewStyle'
        };

        let callCount = 1;
        if(endPoint === 'create' || endPoint === 'bgh' || endPoint === 'edit') callCount = charges;
        if (endpointToCallType[endPoint]) {
            const callType = endpointToCallType[endPoint];
            userData[callType] = (userData[callType] || 0) + callCount;
        }

        // Compare hashed password using SHA-256
        const passwordMatch = await verifyPassword(password, userData.password);
        if (!passwordMatch) {
            return { status: false, message: "Unauthorized User ❌" };
        }

        const oldCredits = userData.credits;
        userData.credits = userData.credits - charges;
        if (userData.credits < 0) {
            userData.credits = 0;
        }

        await updateDoc(doc(db, "Users", docId), {
            credits: userData.credits,
            countCreate: userData.countCreate || 0,
            countBgh: userData.countBgh || 0,
            countEdit: userData.countEdit || 0,
            countUpscale: userData.countUpscale || 0,
            countUpgradePlan: userData.countUpgradePlan || 0,
            countNewProduct: userData.countNewProduct || 0,
            countNewStyle: userData.countNewStyle || 0
        });
        return { status: true, credits: userData.credits, message: "Credits decreased by one successfully ✅" };

    } catch (error) {
        console.error("[decreaseCredits] Error:", error);
        return { status: false, message: error.message };
    }
}


export const checkCouponCode = async (couponCode) => {
    try {
        const couponCodesRef = collection(db, "Coupons");

        const q = query(couponCodesRef, where("coupon_code", "==", couponCode));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "Invalid coupon code ❌", credits: 0 };
        }

        let couponCodeData = null;
        let couponDocId = null;
        querySnapshot.forEach((doc) => {
            couponDocId = doc.id;
            couponCodeData = { id: doc.id, ...doc.data() };
        });

        // Check if coupon is already used
        if (couponCodeData.is_used) {
            return { status: false, message: "Coupon code already used ❌", credits: 0 };
        }

        // Mark coupon as used
        await updateDoc(doc(db, "Coupons", couponDocId), {
            is_used: true
        });

        return { status: true, message: "Coupon code is valid ✅", credits: couponCodeData.credit };
    } catch (error) {
        return { status: false, message: error.message, credits: 0 };
    }
}


export const getUserProfile = async (email) => {
    try {
        const usersRef = collection(db, "Users");
        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "User Not Found ❌" };
        }

        let userData = null;
        querySnapshot.forEach((doc) => {
            userData = { id: doc.id, ...doc.data() };
        });

        return { status: true, user: userData, message: "User Profile Retrieved Successfully ✅" };
    } catch (error) {
        return { status: false, message: error.message };
    }
}

export const updatePassword = async (email, newPassword) => {
    try {
        const usersRef = collection(db, "Users");
        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "User Not Found ❌" };
        }

        let userData = null;
        querySnapshot.forEach((doc) => {
            userData = { id: doc.id, ...doc.data() };
        });

        // Hash the new password using SHA-256
        const hashedPassword = await hashString(newPassword);

        // Update password
        await updateDoc(doc(db, "Users", email), {
            password: hashedPassword
        });

        return { status: true, message: "Password updated successfully ✅" };

    } catch (error) {
        return { status: false, message: error.message };
    }
}


export const updateUserProfile = async (email, updatedData) => {
    try {
        const usersRef = collection(db, "Users");
        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "User Not Found ❌" };
        }

        let docId = null;
        querySnapshot.forEach((doc) => {
            docId = doc.id;
        });

        if (!docId) {
            return { status: false, message: "Error retrieving user document ❌" };
        }

        // Update user profile with the data passed from the component
        await updateDoc(doc(db, "Users", docId), updatedData);

        return { status: true, message: "User profile updated successfully ✅" };

    } catch (error) {
        console.error("Error updating profile:", error);
        return { status: false, message: error.message };
    }
}

export const getUserCredits = async (email) => {
    try {
        // Reference to the Users collection
        const usersRef = collection(db, "Users");

        // Create a query to find the user by email
        const q = query(usersRef, where("email", "==", email));

        // Execute the query
        const querySnapshot = await getDocs(q);

        // Check if user exists
        if (querySnapshot.empty) {
            return { status: false, message: "User not found ❌", credits: 0 };
        }

        // Get user data
        let userData = null;
        querySnapshot.forEach((doc) => {
            userData = { id: doc.id, ...doc.data() };
        });

        // Return the credits or 0 if not defined
        const credits = userData.credits || 0;

        return {
            status: true,
            message: "Credits retrieved successfully",
            credits: credits
        };

    } catch (error) {
        console.error("Error fetching user credits:", error);
        return {
            status: false,
            message: error.message,
            credits: 0
        };
    }
}

// currentPlan="Free Plan"
export const getUserCurrentPlan = async (email) => {
    try {
        const usersRef = collection(db, "Users");
        const q = query(usersRef, where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return { status: false, message: "User Not Found ❌" };
        }

        let userData = null;
        querySnapshot.forEach((doc) => {
            userData = { id: doc.id, ...doc.data() };
        });

        return { status: true, currentPlan: userData.currentPlan || "Free Plan", message: "User Current Plan Retrieved Successfully ✅" };
    } catch (error) {
        return { status: false, message: error.message };
    }
}
